import React, { useEffect, useState, useRef } from "react";
import { ReactGrid, Column, Row } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";
import * as XLSX from "xlsx";

const ExcelEditor = ({
  file,
  onDataUpdate,
  errorRows,
  duplicateImeis,
  imeiColumn,
}) => {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const originalData = useRef(null); // Store original data

  useEffect(() => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const workbook = XLSX.read(e.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

      if (json.length === 0) return;

      originalData.current = json; // Store original data
      updateGridData(json);
    };
    reader.readAsBinaryString(file);
  }, [file]);

  useEffect(() => {
    if (originalData.current) {
      updateGridData(originalData.current);
    }
  }, [errorRows, duplicateImeis, imeiColumn]);

  const updateGridData = (jsonData) => {
    const headers = jsonData[0];
    const dataRows = jsonData;

    const colDefs = headers.map((key, index) => ({
      columnId: key || `col_${index}`,
      width: 150,
    }));

    // Find IMEI column index if exists
    const imeiColIndex = headers.findIndex(
      (header) => header?.toLowerCase() === imeiColumn?.toLowerCase()
    );

    const rowDefs = dataRows.map((row, i) => {
      const isErrorRow = errorRows.includes(i);
      const isDuplicateRow =
        imeiColIndex >= 0 &&
        duplicateImeis.includes(row[imeiColIndex]?.toString() || "");

      return {
        rowId: i.toString(),
        cells: headers.map((header, colIndex) => {
          // Use current edited value if available, otherwise use original
          const currentRow = rows.find((r) => r.rowId === i.toString());
          const cellValue = currentRow
            ? currentRow.cells[colIndex]?.text
            : row[colIndex]?.toString() || "";

          const isImeiColumn =
            imeiColumn && header?.toLowerCase() === imeiColumn.toLowerCase();
          const isDuplicateImei = isImeiColumn && isDuplicateRow;

          // Determine cell class based on error type
          let cellClass = "";
          if (isErrorRow) {
            if (isImeiColumn && isDuplicateImei) {
              cellClass = "imei-error-cell";
            } else {
              cellClass = "row-error";
            }
          } else if (isDuplicateImei) {
            cellClass = "imei-error-cell";
          }

          return {
            type: "text",
            text: cellValue,
            className: cellClass,
          };
        }),
      };
    });

    setColumns(colDefs);
    setRows(rowDefs);
  };

  const handleChanges = (changes) => {
    const updatedRows = [...rows];

    changes.forEach(({ rowId, columnId, newCell }) => {
      const rowIndex = updatedRows.findIndex((r) => r.rowId === rowId);
      const colIndex = columns.findIndex((c) => c.columnId === columnId);

      if (rowIndex > -1 && colIndex > -1) {
        updatedRows[rowIndex].cells[colIndex] = newCell;
      }
    });

    setRows(updatedRows);
    const updatedData = updatedRows.slice(1).map((row) => {
      const obj = {};
      columns.forEach((col, i) => {
        obj[col.columnId] = row.cells[i]?.text || "";
      });
      return obj;
    });

    onDataUpdate(updatedData);
  };

  return (
    <div
      style={{
        maxHeight: "400px",
        overflowY: "auto",
        border: "1px solid #ccc",
        borderRadius: "8px",
      }}
    >
      <ReactGrid
        columns={columns}
        rows={rows}
        onCellsChanged={handleChanges}
        enableRangeSelection
        stickyTopRows={1}
      />
      <style>{`
        .row-error .rg-cell {
          background-color: #ffe6e6 !important;
        }
        .imei-error-cell, .imei-error-cell .rg-cell {
          background-color: #ffcccc !important;
          border: 2px solid #ff0000 !important;
        }
        .header-cell {
          background-color: #0f6936 !important;
          color: white !important;
          font-weight: bold !important;
        }
      `}</style>
    </div>
  );
};

export default ExcelEditor;
